// src/features/dashboard/components/ActivityDetailsModal.tsx

import {
  useEffect,
  useState,
} from 'react';

import type {
  ActivityDetailsModalProps,
  EmployeeDayDetailsResponse,
} from '../types/activityDetails.types';

import {
  getApplications,
  getDistributionItems,
  getKpiCards,
  formatDate,
  formatHours,
} from '../mocks/activityDetails.mock';

import {
  getAppsCountText,
} from '../utils/activityDetails.utils';

import {
  getEmployeeDayDetails,
} from '../services/teamTableApi';

const ActivityDetailsModal = ({
  open,
  onClose,
  employeeName,
  employeeCode,
  loggedDate,
}: ActivityDetailsModalProps) => {

  const [
    loading,
    setLoading,
  ] = useState(false);

  const [
    activityData,
    setActivityData,
  ] =
    useState<EmployeeDayDetailsResponse | null>(
      null,
    );

  /**
   * =========================================
   * API CALL
   * =========================================
   */
  useEffect(() => {

    const loadData =
      async () => {

        if (
          !open ||
          !employeeCode ||
          !loggedDate
        ) {
          return;
        }

        try {

          setLoading(true);

          const response =
            await getEmployeeDayDetails(
              employeeCode,
              loggedDate,
            );

          console.log(
            'EMPLOYEE DETAILS RESPONSE',
            response,
          );

          setActivityData(
            response,
          );

        } catch (error) {

          console.error(
            'EMPLOYEE DETAILS API ERROR',
            error,
          );

        } finally {

          setLoading(false);
        }
      };

    loadData();

  }, [
    open,
    employeeCode,
    loggedDate,
  ]);

  /**
   * =========================================
   * CLOSE
   * =========================================
   */
  if (!open) {
    return null;
  }

  /**
   * =========================================
   * SAFE DATA
   * =========================================
   */
  const safeData: EmployeeDayDetailsResponse =

    activityData || {

      summary: {

        employeeCode: '',

        fullName: '',

        loggedDate: '',

        toT_LOGGEDHOURS: 0,

        toT_ACTIVEHOURS: 0,

        toT_PRODHOURS: 0,

        toT_TIMEONSYSTEM: 0,

        toT_TIMEAWAYFROMSYSTEM: 0,

        toT_IDLETIME: 0,

        firstlogin: '',

        lastlogout: '',

        attendanceStatus: '',

        productivityPercentage: 0,
      },

      applications: [],

      distribution: {

        productivePercentage: 0,

        nonProductivePercentage: 0,

        neutralPercentage: 0,

        idlePercentage: 0,
      },
    };

  /**
   * =========================================
   * UI DATA
   * =========================================
   */
  const KPI_CARDS =
    getKpiCards(
      safeData,
    );

  const distributionItems =
    getDistributionItems(
      safeData,
    );

  const applications =
    getApplications(
      safeData,
    );

  return (

    <div
      className='
        fixed
        inset-0
        z-[999]
        flex
        items-center
        justify-center
        bg-black/40
        p-4
      '
    >

      <div
        className='
          flex
          max-h-[88vh]
          w-full
          max-w-[760px]
          flex-col
          overflow-hidden
          rounded-[18px]
          bg-white
          shadow-2xl
        '
      >

        {/* HEADER */}
        <div
          className='
            flex
            items-center
            justify-between
            border-b
            border-[#E5E7EB]
            px-5
            py-4
          '
        >

          <div
            className='
              flex
              items-center
              gap-3
            '
          >

            <h2
              className='
                text-[18px]
                font-bold
                text-[#002B5B]
              '
            >
              {safeData.summary.fullName ||
                employeeName}
            </h2>

            <div
              className='
                rounded-full
                bg-[#EEF4FF]
                px-3
                py-1
                text-[12px]
                font-medium
                text-[#3366FF]
              '
            >
              {formatDate(
                safeData.summary.loggedDate,
              )}
            </div>

          </div>

          <button
            onClick={onClose}
            className='
              text-[22px]
              text-[#667085]
            '
          >
            ×
          </button>

        </div>

        {/* BODY */}
        <div
          className='
            overflow-y-auto
            p-5
          '
        >

          {loading ? (

            <div
              className='
                flex
                h-[300px]
                items-center
                justify-center
                text-[16px]
                font-semibold
              '
            >
              Loading...
            </div>

          ) : (

            <>

              {/* KPI */}
              <div
                className='
                  grid
                  grid-cols-2
                  gap-3
                  md:grid-cols-4
                '
              >

                {KPI_CARDS.map(
                  (
                    item,
                    index,
                  ) => (

                    <div
                      key={index}
                      className='
                        min-w-0
                        rounded-[12px]
                        border
                        border-[#E5E7EB]
                        p-4
                      '
                    >

                      <p
                        className='
                          overflow-hidden
                          text-ellipsis
                          whitespace-nowrap
                          text-[10px]
                          font-bold
                          uppercase
                          tracking-wide
                          text-[#98A2B3]
                        '
                        title={item.title}
                      >
                        {item.title}
                      </p>

                      <h3
                        className={`
                          mt-2
                          whitespace-nowrap
                          text-[18px]
                          font-bold
                          ${item.color}
                        `}
                      >
                        {item.value}
                      </h3>

                    </div>

                  ),
                )}

              </div>

              {/* DISTRIBUTION */}
              <div className='mt-6'>

                <div
                  className='
                    mb-3
                    flex
                    items-center
                    justify-between
                  '
                >

                  <h3
                    className='
                      text-[18px]
                      font-bold
                      text-[#002B5B]
                    '
                  >
                    Time Distribution
                  </h3>

                  <span
                    className='
                      text-[12px]
                      font-semibold
                      text-[#667085]
                    '
                  >
                    {formatHours(
                      safeData.summary
                        .toT_LOGGEDHOURS,
                    )} total
                  </span>

                </div>

                {/* BAR */}
                <div
                  className='
                    flex
                    h-[12px]
                    overflow-hidden
                    rounded-full
                    bg-[#F3F4F6]
                  '
                >

                  {distributionItems.map(
                    (
                      item,
                      index,
                    ) => (

                      <div
                        key={index}
                        style={{
                          width:
                            item.width,
                          backgroundColor:
                            item.color,
                        }}
                      />

                    ),
                  )}

                </div>

                {/* LEGEND */}
                <div
                  className='
                    mt-4
                    flex
                    flex-wrap
                    gap-4
                  '
                >

                  {distributionItems.map(
                    (
                      item,
                      index,
                    ) => (

                      <div
                        key={index}
                        className='
                          flex
                          items-center
                          gap-2
                        '
                      >

                        <div
                          className='
                            h-[10px]
                            w-[10px]
                            rounded-[2px]
                          '
                          style={{
                            backgroundColor:
                              item.color,
                          }}
                        />

                        <span
                          className='
                            text-[12px]
                            text-[#475467]
                          '
                        >
                          {item.label}
                        </span>

                        <span
                          className='
                            text-[12px]
                            font-bold
                            text-[#111827]
                          '
                        >
                          {item.percentage}
                        </span>

                      </div>

                    ),
                  )}

                </div>

              </div>

              {/* APPLICATIONS */}
              <div className='mt-7'>

                <div
                  className='
                    mb-4
                    flex
                    items-center
                    justify-between
                  '
                >

                  <h3
                    className='
                      text-[18px]
                      font-bold
                      text-[#002B5B]
                    '
                  >
                    Application Usage
                  </h3>

                  <span
                    className='
                      text-[12px]
                      font-semibold
                      text-[#667085]
                    '
                  >
                    {getAppsCountText(
                      applications.length,
                    )}
                  </span>

                </div>

                <div className='space-y-3'>

                  {applications.map(
                    (
                      app,
                      index,
                    ) => (

                      <div
                        key={index}
                        className='
                          flex
                          items-center
                          justify-between
                          rounded-[10px]
                          border
                          border-[#E5E7EB]
                          px-3
                          py-2.5
                        '
                      >

                        <div
                          className='
                            flex
                            items-center
                            gap-3
                          '
                        >

                          <div
                            className='
                              flex
                              h-[32px]
                              w-[32px]
                              items-center
                              justify-center
                              rounded-[8px]
                              text-[10px]
                              font-bold
                              text-white
                            '
                            style={{
                              backgroundColor:
                                app.color,
                            }}
                          >
                            {app.initials}
                          </div>

                          <div>

                            <h4
                              className='
                                text-[13px]
                                font-semibold
                                text-[#111827]
                              '
                            >
                              {app.app}
                            </h4>

                            <span
                              className={`
                                mt-1
                                inline-block
                                rounded-full
                                px-2
                                py-[2px]
                                text-[10px]
                                font-semibold
                                ${app.statusColor}
                              `}
                            >
                              {app.status}
                            </span>

                          </div>

                        </div>

                        <div
                          className='
                            text-[13px]
                            font-bold
                            text-[#111827]
                          '
                        >
                          {app.time}
                        </div>

                      </div>

                    ),
                  )}

                </div>

              </div>

            </>
          )}

        </div>

      </div>

    </div>
  );
};

export default ActivityDetailsModal;